import { useAppDispatch, useAppSelector } from 'store'
import { useCallback, useState } from 'react'
import { RequestContext } from 'utils/pos'
import { Customer, order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import { convertUserToCustomer } from 'utils/customer'
import {
  customerAddedToSale,
  customerRemovedFromSale,
  setCustomer,
} from 'reducers/customer'
import { err, ok, Result } from 'utils/result'
import { loyaltyApi } from 'api'
import { getErrorMessage, isApiError } from 'utils/api'
import { useLoyalty } from './loyalty'
import { getOrderDiscount, AppliedDiscount } from 'utils/loyalty'

export function useCustomer() {
  return useAppSelector(state => state.customer.customer)
}

export function useCustomerContext() {
  return useAppSelector(state => state.customer.context)
}

export function useIsCustomerAddedToSale(): boolean {
  return useAppSelector(state => state.customer.addedToSale)
}

type CustomerState = 'init' | 'pending' | 'success' | 'error'
/**
 * Custom hook to get customer information.
 *
 * This hook provides a function to fetch customer data based on the context
 * (SDK or API) and an optional reference ID. It manages the state of the
 * request (init, pending, success, error) and handles errors.
 *
 * @returns {Object} An object containing the getCustomer function, the current state, and any error encountered.
 */
export function useGetCustomer(): {
  getCustomer: (
    context: RequestContext,
    referenceId?: string
  ) => Promise<Result<Customer, Error>>
  state: CustomerState
} {
  const [state, setState] = useState<CustomerState>('init')
  const dispatch = useAppDispatch()
  const [getUser] = loyaltyApi().useLazyGetUserQuery()

  const getCustomer = useCallback(
    async (
      context: RequestContext,
      referenceId?: string
    ): Promise<Result<Customer, Error>> => {
      setState('pending')
      try {
        if (context === RequestContext.SDK) {
          const customer = await squareOrderApi.getCustomer()

          // Making this forward compatible when the SDK returns undefined customer.
          if (!customer) {
            throw new Error('No customer has been added to sale.')
          }

          dispatch(setCustomer({ customer, context }))

          // Add customer to sale because if customer exists in the SDK, customer
          // has already been added to sale.
          dispatch(customerAddedToSale())

          setState('success')
          return ok(customer)
        }

        if (!referenceId) {
          throw new Error('No customer has been added to sale.')
        }

        const { user } = await getUser(referenceId).unwrap()
        const customer = convertUserToCustomer(user)

        dispatch(
          setCustomer({
            context,
            customer,
          })
        )
        setState('success')
        return ok(customer)
      } catch (error) {
        setState('error')
        if (isApiError(error)) {
          return err(new Error(getErrorMessage(error)))
        }
        return err(error as Error)
      }
    },
    [dispatch, getUser]
  )

  return {
    getCustomer,
    state,
  }
}

export function useAddCustomerToSale() {
  const dispatch = useAppDispatch()
  const isCustomerAddedToSale = useIsCustomerAddedToSale()

  const addCustomerToSale = useCallback(
    async (customer: Customer | null): Promise<Result<Customer, Error>> => {
      try {
        if (isCustomerAddedToSale) {
          throw new Error(
            'Unexpected application state. Customer is already added.'
          )
        }

        if (!customer) {
          throw new Error(
            'Unexpected application state. Customer should not be undefined.'
          )
        }

        await squareOrderApi.addCustomer(customer)
        dispatch(customerAddedToSale())
        return ok(customer)
      } catch (error) {
        return err(error as Error)
      }
    },
    [dispatch, isCustomerAddedToSale]
  )

  return {
    addCustomerToSale,
  }
}

export function useRemoveCustomerFromSale() {
  const dispatch = useAppDispatch()
  const customer = useCustomer()
  const isCustomerAddedToSale = useIsCustomerAddedToSale()
  const { removeOffer } = useLoyalty()

  const removeCustomerFromSale = useCallback(async (): Promise<
    Result<true, Error>
  > => {
    try {
      if (!customer) {
        throw new Error(
          'Unexpected application state. Customer should not be undefined.'
        )
      }

      if (!isCustomerAddedToSale) {
        throw new Error(
          'Unexpected application state. There is no customer to remove.'
        )
      }

      await squareOrderApi.removeCustomer()
      dispatch(customerRemovedFromSale())

      const orderDiscount = await getOrderDiscount()

      if (orderDiscount?.uid) {
        const appliedDiscount = {
          offerId: orderDiscount.metadata?.offerId,
          redemptionId: orderDiscount.metadata?.redemptionId,
          discountId: orderDiscount.uid,
        } as AppliedDiscount

        await Promise.all([
          removeOffer(appliedDiscount, customer.referenceId),
          squareOrderApi.removeDiscount(orderDiscount.uid),
        ])
      }

      return ok(true)
    } catch (error) {
      return err(error as Error)
    }
  }, [customer, dispatch, isCustomerAddedToSale, removeOffer])

  return {
    removeCustomerFromSale,
  }
}
